/**
 * KAN-776: режим партии в форме "Архив от продавца": два и более архива.
 * Сразу запрашивается план (import-batch/plan/): таблица файлов и сводка по
 * странам; общие поля партии (метка, флаги, продавец обязателен, стоимость,
 * галочки импорта), кнопка "Подключить N аккаунтов" (N: строки без ошибки,
 * не дубли и с прокси). После старта (202, batch_id) панель хода BatchProgress.
 * Прокси-пикера нет: прокси назначает сервер по стране номера.
 * KAN-777: на старт уходят только файлы строк, которые войдут в партию
 * (importableFiles): дубли и строки без прокси или с ошибкой отбрасываются;
 * под сводкой строка "Дубли: N (...)".
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { BatchCommonFields } from '@/components/telegram/BatchCommonFields';
import { BatchPlanSummary, BatchPlanTable } from '@/components/telegram/BatchPlanTable';
import { BatchProgress } from '@/components/telegram/BatchProgress';
import { ConnectSellerFields } from '@/components/telegram/ConnectSellerFields';
import { ImportFlagsFields } from '@/components/telegram/ImportFlagsFields';
import { PurchaseCostField } from '@/components/telegram/PurchaseCostField';
import {
  DEFAULT_IMPORT_FLAGS,
  describeApiError,
  skipSpamCheckFromFlag,
  validatePurchaseCost,
  type ImportFlags,
} from '@/lib/telegramConnect';
import {
  DEFAULT_BATCH_COMMON,
  countDuplicates,
  countImportable,
  describeDuplicates,
  describeProxyWarnings,
  importableFiles,
  partitionArchiveFiles,
  pluralAccounts,
  validateBatchFiles,
  type BatchCommonValues,
} from '@/lib/telegramImportBatch';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';

interface Props {
  files: File[];
  /** KAN-752: выбранная кампания (оператор); пусто - не передаётся. */
  operatorId?: string;
  onClose: () => void;
  /** Вернуться к пустой форме (другие файлы). */
  onReset: () => void;
  /** Партия запущена: мастер прячет зону загрузки. */
  onStarted?: () => void;
  /** Запрос старта в полёте: мастер блокирует зону загрузки. */
  onPendingChange?: (pending: boolean) => void;
}

/** Ключ запроса плана: тот же набор файлов не разбирается повторно. */
function filesKey(files: File[]): string {
  return files.map((f) => `${f.name}:${f.size}:${f.lastModified}`).join('|');
}

export function ImportArchiveBatch({
  files,
  operatorId,
  onClose,
  onReset,
  onStarted,
  onPendingChange,
}: Props): JSX.Element {
  const { accepted, rejected } = useMemo(() => partitionArchiveFiles(files), [files]);
  // лимиты партии (30 файлов, 200 МБ суммарно): при ошибке план не запрашивается
  const filesError = validateBatchFiles(accepted);
  const [common, setCommon] = useState<BatchCommonValues>(DEFAULT_BATCH_COMMON);
  const [flags, setFlags] = useState<ImportFlags>(DEFAULT_IMPORT_FLAGS);
  const [sellerId, setSellerId] = useState('');
  const [purchaseCost, setPurchaseCost] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);

  const plan = useQuery({
    queryKey: ['telegram-import-batch-plan', operatorId ?? '', filesKey(accepted)],
    queryFn: () => telegramAccountsApi.planBatchImport(accepted, operatorId),
    enabled: accepted.length > 0 && !filesError,
    retry: false,
    staleTime: Infinity,
  });

  const start = useMutation({
    // KAN-777: только файлы строк, которые войдут в партию (то же число, что на кнопке)
    mutationFn: () =>
      telegramAccountsApi.startBatchImport(importableFiles(accepted, plan.data?.items ?? []), {
        sellerId,
        operatorId,
        enabled: common.enabled,
        outreach: common.outreach,
        skipSpamCheck: skipSpamCheckFromFlag(flags.checkSpam),
        had2fa: flags.had2fa,
        purchaseCost,
        labelPrefix: common.labelPrefix,
      }),
    onSuccess: (res) => {
      setBatchId(res.batch_id);
      onStarted?.();
    },
    onError: (err: unknown) => setError(describeApiError(err, 'Не удалось запустить партию')),
  });

  const starting = start.isPending;
  useEffect(() => {
    onPendingChange?.(starting);
  }, [starting, onPendingChange]);

  if (batchId) return <BatchProgress batchId={batchId} onDone={onClose} />;

  const items = plan.data?.items ?? [];
  const importable = countImportable(items);
  const costError = validatePurchaseCost(purchaseCost);
  const planError = plan.error ? describeApiError(plan.error, 'ошибка сервера') : null;
  const proxyWarning = describeProxyWarnings(plan.data?.proxy_warnings);
  const duplicatesLine = describeDuplicates(countDuplicates(items, plan.data));
  const canSubmit =
    !filesError &&
    importable > 0 &&
    !!sellerId &&
    !costError &&
    !plan.isPending &&
    !start.isPending;
  const disabled = start.isPending;

  return (
    <div className="space-y-4" data-testid="archive-batch">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-medium">Партия: {pluralAccounts(accepted.length)} в архивах</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="batch-reset-button"
          disabled={disabled}
          onClick={onReset}
        >
          Выбрать другие файлы
        </Button>
      </div>

      {filesError ? (
        <Alert variant="destructive" data-testid="batch-files-error">
          <AlertDescription>{filesError}</AlertDescription>
        </Alert>
      ) : (
        <BatchPlanTable
          items={items}
          rejected={rejected}
          pending={plan.isPending && accepted.length > 0}
          error={planError}
        />
      )}
      <BatchPlanSummary summary={plan.data?.summary} />
      {duplicatesLine && (
        <p className="text-xs text-gray-600" data-testid="batch-plan-duplicates">
          {duplicatesLine}
        </p>
      )}
      {proxyWarning && (
        <p className="text-xs text-amber-700" data-testid="batch-proxy-warnings">
          {proxyWarning}
        </p>
      )}

      <BatchCommonFields
        values={common}
        disabled={disabled}
        onChange={(patch) => setCommon((prev) => ({ ...prev, ...patch }))}
      />

      <ConnectSellerFields
        sellerId={sellerId}
        disabled={disabled}
        onChange={setSellerId}
        required
      />

      <PurchaseCostField value={purchaseCost} disabled={disabled} onChange={setPurchaseCost} />

      <ImportFlagsFields
        idPrefix="batch"
        values={flags}
        disabled={disabled}
        onChange={(patch) => setFlags((prev) => ({ ...prev, ...patch }))}
      />

      {error && (
        <Alert variant="destructive" data-testid="connect-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end">
        <Button
          data-testid="connect-batch-button"
          disabled={!canSubmit}
          onClick={() => {
            setError(null);
            start.mutate();
          }}
        >
          {start.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Подключить {pluralAccounts(importable)}
        </Button>
      </div>
    </div>
  );
}
